// src/utils/roles.js

export const ROLES = Object.freeze({
  SUPERADMIN: "superadmin",
  ADMIN: "admin",
  LOCATION_ADMIN: "locationAdmin",
  AUDITOR: "auditor",
  USER: "user",
  TECHNICIAN: "technician",
});

/**
 * Human-readable presentation labels associated with each role key.
 */
export const ROLE_LABELS = Object.freeze({
  [ROLES.SUPERADMIN]: "Super Admin",
  [ROLES.ADMIN]: "Admin",
  [ROLES.LOCATION_ADMIN]: "Location Admin",
  [ROLES.AUDITOR]: "Auditor",
  [ROLES.USER]: "User",
  [ROLES.TECHNICIAN]: "Technician",
});

/**
 * Roles a Location Administrator is authorized to assign.
 */
export const LOCATION_ADMIN_ASSIGNABLE_ROLES = Object.freeze([
  { value: ROLES.USER, label: ROLE_LABELS[ROLES.USER] },
  { value: ROLES.AUDITOR, label: ROLE_LABELS[ROLES.AUDITOR] },
  { value: ROLES.TECHNICIAN, label: ROLE_LABELS[ROLES.TECHNICIAN] },
]);
/**
 * Roles a regular administrator (Admin) is authorized to assign.
 */
export const ADMIN_ASSIGNABLE_ROLES = Object.freeze([
  { value: ROLES.USER, label: ROLE_LABELS[ROLES.USER] },
   { value: ROLES.AUDITOR, label: ROLE_LABELS[ROLES.AUDITOR] },
  { value: ROLES.TECHNICIAN, label: ROLE_LABELS[ROLES.TECHNICIAN] },
  { value: ROLES.LOCATION_ADMIN, label: ROLE_LABELS[ROLES.LOCATION_ADMIN] },
  { value: ROLES.ADMIN, label: ROLE_LABELS[ROLES.ADMIN] },
]);
/**
 * Roles a Super Administrator is authorized to assign.
 */
export const SUPERADMIN_ASSIGNABLE_ROLES = Object.freeze([
  { value: ROLES.USER, label: ROLE_LABELS[ROLES.USER] },
  { value: ROLES.AUDITOR, label: ROLE_LABELS[ROLES.AUDITOR] },
  { value: ROLES.TECHNICIAN, label: ROLE_LABELS[ROLES.TECHNICIAN] },
  { value: ROLES.LOCATION_ADMIN, label: ROLE_LABELS[ROLES.LOCATION_ADMIN] },
  { value: ROLES.ADMIN, label: ROLE_LABELS[ROLES.ADMIN] },
  { value: ROLES.SUPERADMIN, label: ROLE_LABELS[ROLES.SUPERADMIN] },
]);

/**
 * Normalizes any variation of role string into standard system role
 */
export const normalizeRole = (role) => {
  if (!role) return ROLES.USER;
  const roleStr = String(role).trim().toLowerCase().replace(/[\s_-]+/g, "");
  if (roleStr === "superadmin") return ROLES.SUPERADMIN;
  if (roleStr === "locationadmin" || roleStr === "location") return ROLES.LOCATION_ADMIN;
  if (roleStr === "admin") return ROLES.ADMIN;
   if (roleStr === "auditor") return ROLES.AUDITOR;
  if (roleStr === "technician") return ROLES.TECHNICIAN;

  return ROLES.USER;
};

/**
 * Dynamically resolves list of role assignment options based on the editor's permissions.
 */
export const getAssignableRoles = (currentUserRole) => {
  const normalized = normalizeRole(currentUserRole);
  if (normalized === ROLES.SUPERADMIN) return SUPERADMIN_ASSIGNABLE_ROLES;
  if (normalized === ROLES.ADMIN) return ADMIN_ASSIGNABLE_ROLES;
  if (normalized === ROLES.LOCATION_ADMIN) return LOCATION_ADMIN_ASSIGNABLE_ROLES; // ✅ FIXED
  return []; 
};

/**
 * Returns true if callerRole is permitted to assign targetRole.
 */
export const canAssignRole = (callerRole, targetRole) => {
  const normalizedTarget = normalizeRole(targetRole);
  const allowed = getAssignableRoles(callerRole).map((r) => r.value);
  return allowed.includes(normalizedTarget);
};

/**
 * Checks if a user's role satisfies the allowed roles list.
 * Superadmins automatically bypass all role checks.
 */
export const hasRequiredRole = (userRole, allowedRoles = []) => {
  if (!allowedRoles || allowedRoles.length === 0) return true;
  const normalizedUserRole = normalizeRole(userRole);

  if (normalizedUserRole === ROLES.SUPERADMIN) return true;

  const normalizedAllowed = allowedRoles.map(normalizeRole);
  return normalizedAllowed.includes(normalizedUserRole);
};